import { useMemo } from 'react'
import CruiseLuxPlusLabel from 'components/Cruises/CruiseLuxPlusLabel'
import Group from 'components/utils/Group'
import { getIsEnabledLuxPlusCruiseInclusions } from 'luxPlus/selectors/featureToggle'
import CruiseLuxPlusOnboardCreditLabel from 'components/Cruises/CruiseInclusion/CruiseLuxPlusOnboardCreditLabel'
import { useAppSelector } from 'hooks/reduxHooks'
import { buildAllStandardInclusions } from 'lib/cruises/cruiseInclusionsUtils'
import CruiseBookingConditionsInfo from './CruiseBookingConditionsInfo'
import CruiseLimitedInclusionList from './CruiseLimitedInclusionList'

const VISIBLE_INCLUSIONS = 3

interface Props {
  evergreenInclusions?: Array<App.PackageInclusion>
  tileInclusions?: Array<App.OfferInclusion>
  standardInclusions: Array<App.PackageInclusion>
  luxPlusInclusions: Array<App.PackageInclusion>
  canSeeLuxPlusLabel?: boolean
  offer?: App.CruiseOffer
  showMoreOnSeparateLine?: boolean
}

function CruiseInclusionListHomeTile({
  standardInclusions,
  evergreenInclusions,
  tileInclusions,
  luxPlusInclusions,
  canSeeLuxPlusLabel,
  offer,
  showMoreOnSeparateLine,
}: Props) {
  const isEnabledLuxPlusCruiseInclusions = useAppSelector(getIsEnabledLuxPlusCruiseInclusions)

  const promotionalInclusions = useMemo(
    () => buildAllStandardInclusions(standardInclusions, tileInclusions, true),
    [standardInclusions, tileInclusions],
  )
  const allInclusions = useMemo(
    () => [...promotionalInclusions, ...(evergreenInclusions || [])],
    [promotionalInclusions, evergreenInclusions],
  )

  const bookingConditions = useMemo(() => {
    return promotionalInclusions.filter((inclusion) => inclusion.displayType === 'BOOKING_CONDITION')
  }, [promotionalInclusions])

  const benefitInclusions = useMemo(() => {
    return allInclusions.filter((inclusion) => inclusion.displayType !== 'BOOKING_CONDITION')
  }, [allInclusions])
  const promotionalBenefitInclusionIds = useMemo(
    () => promotionalInclusions
      .filter((inclusion) => inclusion.displayType !== 'BOOKING_CONDITION')
      .map((inclusion) => inclusion.id),
    [promotionalInclusions],
  )

  const hasLuxPlusInclusions = isEnabledLuxPlusCruiseInclusions && luxPlusInclusions.length > 0
  const hasNormalInclusions = benefitInclusions.length > 0
  const hasInclusions = hasNormalInclusions || hasLuxPlusInclusions || canSeeLuxPlusLabel

  return <>
    {hasInclusions && <Group direction="vertical" gap={16} horizontalAlign="start">
      {(canSeeLuxPlusLabel || hasLuxPlusInclusions) && (
        <Group direction="vertical" gap={8} verticalAlign="center">
          {canSeeLuxPlusLabel && <CruiseLuxPlusLabel
            isModalEnabled
            offer={offer}
          />}

          {hasLuxPlusInclusions && (
            <CruiseLuxPlusOnboardCreditLabel
              luxPlusInclusions={luxPlusInclusions}
              offer={offer}
            />
          )}
        </Group>
      )}

      {hasNormalInclusions && <CruiseLimitedInclusionList
        inclusions={benefitInclusions}
        showMoreOnSeparateLine={showMoreOnSeparateLine}
        visibleItems={VISIBLE_INCLUSIONS}
        endIcon={promotionalBenefitInclusionIds.length > 0 && bookingConditions.length > 0 &&
          <CruiseBookingConditionsInfo conditions={bookingConditions} />}
        endIconInclusionIds={promotionalBenefitInclusionIds}
      />}
    </Group>}
  </>
}

export default CruiseInclusionListHomeTile
